import { useCallback, useEffect, useMemo, useState } from "react";
import { TableColumnsType } from "antd";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { baseQueryInfo } from "utils/queryUtil";
import {
  EVENT_HISTORY_CHANGE_UNIT,
  EVENT_HISTORY_TYPE,
  UsageLayoutOutletContext,
} from "api/interfaces/subscription.interface";
import { useOutletContext } from "react-router-dom";
import EventToHistoryPresenter from "./EventToHistoryPresenter";
import * as s from "./EventToHistoryStyled";
import useTableQueryParams from "hook/useTableQueryParams";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  EventHistoryDto,
  GetPlanEventHistoryResponse,
} from "api/interfaces/planInterface.interface";
import {
  buildEventToHistoryColumns,
  eventLabelMap,
  usePlanEventHistoryQuery,
} from "./eventToHistoryCommon";

const initialQueryInfo: BasePagination = {
  ...baseQueryInfo,
  sortType: "createdAt",
  sortDirection: SORT_DIRECTION.DESC,
};

export default function EventToHistoryContainer() {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { onHeaderCell, setButtons, onChangePage, setStatus, updateQueryInfo } =
    useOutletContext<UsageLayoutOutletContext>();
  const { searchParams } = useTableQueryParams();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});

  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      type: get("type") ?? undefined,
    };
  }, [pageInfo, searchParams]);

  const { data, isFetching } = usePlanEventHistoryQuery({
    accountId: selectedAccount.accountId,
    queryInfo,
    enabled: !!selectedAccount.accountId,
    onSuccess: (res: GetPlanEventHistoryResponse) => {
      if (res.page) {
        setStatus({ totalTitle: "Total", totalValue: res.page.total });
        setPageInfo((query) => {
          return {
            ...query,
            ...res.page,
          };
        });
      }
    },
  });

  const rows = useMemo<EventHistoryDto[]>(() => {
    if (data?.result && data.error === 0) {
      return data.result;
    }
    return [];
  }, [data]);

  const onChangeType = useCallback(
    (value: string | undefined) => {
      if (value === undefined) return;
      updateQueryInfo({
        type: value === "all" ? null : value,
        pageNum: 0,
      });
    },
    [updateQueryInfo]
  );

  const columns: TableColumnsType<EventHistoryDto> = useMemo(
    () =>
      buildEventToHistoryColumns({
        selectedAccount,
        queryInfo,
        onHeaderCell,
        onChangeType,
      }),
    [onChangeType, onHeaderCell, queryInfo, selectedAccount]
  );

  useEffect(() => {
    setButtons(null);
  }, [setButtons]);

  return (
    <EventToHistoryPresenter
      columns={columns}
      data={rows}
      onChangePage={onChangePage}
      queryInfo={queryInfo}
      isFetching={isFetching}
    />
  );
}
